import { useState, useEffect, useMemo, Fragment } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Box,
  IconButton,
  TableSortLabel,
  Collapse,
  Grid,
  Button,
} from '@mui/material';
import { ExpandMore, ExpandLess, OpenInNew } from '@mui/icons-material';
import { format } from 'date-fns';
import {
  getTasksView,
  TasksViewFilters,
  TasksViewResponse,
  TaskViewActionItem,
  DashboardApiError,
} from '../../services/dashboardApi';
import { isIntegerUserId } from '../../services/authApi';

interface TasksViewTableProps {
  type: 'user' | 'airport' | 'system';
  userId?: number | string; // May be GUID (string) or integer (number)
  airportId?: number;
  /** When true, the Tasks section starts expanded. Default false. Set true for non-admin, non-manager users. */
  defaultExpanded?: boolean;
  initialFilters?: {
    pastDueOnly?: boolean;
    upcomingDays?: number;
    status?: string;
    myInProgressActionsOnly?: boolean;
  };
  /** When provided (e.g. for tenant/client users), merged into initial filters so the first API load only shows tasks with user's IN_PROGRESS actions. */
  defaultFiltersForTenantOrClientUser?: { status: string; myInProgressActionsOnly: boolean };
}

type SortField = 'dueDate' | 'projectName' | 'phaseName' | 'taskName' | 'status';
type SortOrder = 'asc' | 'desc';

export default function TasksViewTable({
  type,
  userId,
  airportId,
  defaultExpanded = false,
  initialFilters,
  defaultFiltersForTenantOrClientUser,
}: TasksViewTableProps) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TasksViewResponse['tasks']>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('dueDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [filters, setFilters] = useState<TasksViewFilters>({
    page: 1,
    pageSize: 25,
    ...initialFilters,
    ...(defaultFiltersForTenantOrClientUser || {}),
  });
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    const loadTasks = async () => {
      try {
        setLoading(true);
        setError(null);

        // For airport type, wait for airportId to be available
        if (type === 'airport' && !airportId) {
          // Don't show error immediately - airportId might still be loading
          // Keep loading state until airportId is available
          setLoading(true);
          return;
        }

        // Skip API call if userId is a GUID (backend should return integer, but may fallback to GUID)
        if (type === 'user' && userId && typeof userId === 'string') {
          setError('Tasks view unavailable: userId format not supported (GUID fallback)');
          setLoading(false);
          return;
        }

        const requestFilters: TasksViewFilters = {
          ...filters,
          userId: type === 'user' && userId && isIntegerUserId(userId) ? userId : undefined,
          airportId: type === 'airport' && airportId ? airportId : undefined,
          myInProgressActionsOnly: filters.myInProgressActionsOnly,
          // System-wide: no userId or airportId
        };

        const response = await getTasksView(requestFilters);
        setTasks(response.tasks);
        setPagination(response.pagination);
      } catch (err) {
        const apiError = err as DashboardApiError;
        setError(apiError.message || 'Failed to load tasks');
        console.error('Error loading tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [type, userId, airportId, filters]);

  // Update filters when initialFilters change
  useEffect(() => {
    if (initialFilters) {
      setFilters((prev) => ({
        ...prev,
        ...initialFilters,
        page: 1, // Reset to first page when filters change
      }));
    }
  }, [initialFilters]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedTasks = useMemo(() => {
    const sorted = [...tasks];
    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'dueDate': {
          const aDate = new Date(a.contractedEndDate).getTime();
          const bDate = new Date(b.contractedEndDate).getTime();
          comparison = aDate - bDate;
          break;
        }
        case 'projectName':
          comparison = a.projectName.localeCompare(b.projectName);
          break;
        case 'phaseName':
          comparison = a.phaseName.localeCompare(b.phaseName);
          break;
        case 'taskName':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    // Always show overdue tasks first
    return sorted.sort((a, b) => {
      if (a.isPastDue && !b.isPastDue) return -1;
      if (!a.isPastDue && b.isPastDue) return 1;
      return 0;
    });
  }, [tasks, sortField, sortOrder]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (task: TasksViewResponse['tasks'][0]) => {
    navigate(`/execution?projectId=${task.projectId}`);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'COMPLETE':
        return 'success';
      case 'IN_PROGRESS':
        return 'primary';
      case 'NOT_STARTED':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading && tasks.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper>
      <Box sx={{ p: 2, borderBottom: expanded ? 1 : 0, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Tasks</Typography>
          {!expanded && (
            <Chip
              label={`${pagination.total} total`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      
      {/* Minimized View - Summary */}
      {!expanded && (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {pagination.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Tasks
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {tasks.filter(t => t.isPastDue).length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Overdue
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {tasks.filter(t => t.daysUntilDeadline !== undefined && t.daysUntilDeadline <= 7).length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Due This Week
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {tasks.filter(t => t.status === 'IN_PROGRESS').length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  In Progress
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Expanded View - Full Table */}
      <Collapse in={expanded}>
        <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'dueDate'}
                  direction={sortField === 'dueDate' ? sortOrder : 'asc'}
                  onClick={() => handleSort('dueDate')}
                >
                  Due Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'projectName'}
                  direction={sortField === 'projectName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('projectName')}
                >
                  Project
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'phaseName'}
                  direction={sortField === 'phaseName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('phaseName')}
                >
                  Phase
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'taskName'}
                  direction={sortField === 'taskName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('taskName')}
                >
                  Task
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'status'}
                  direction={sortField === 'status' ? sortOrder : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Days Overdue / Until Deadline</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No tasks found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedTasks.map((task) => {
                const actions = task.actions ?? [];
                return (
                  <Fragment key={task.taskId}>
                    {/* Task row */}
                    <TableRow
                      key={task.taskId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(task)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: task.isPastDue ? 'error.main' : 'text.primary',
                            fontWeight: task.isPastDue ? 'bold' : 'normal',
                          }}
                        >
                          {format(new Date(task.contractedEndDate), 'MMM dd, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/execution?projectId=${task.projectId}`}
                          style={{ textDecoration: 'none' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'primary.main',
                              fontWeight: 500,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {task.projectName}
                          </Typography>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{task.phaseName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{task.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.status.replace('_', ' ')}
                          size="small"
                          color={getStatusColor(task.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {task.isPastDue && task.daysOverdue !== undefined ? (
                          <Typography variant="body2" color="error">
                            {task.daysOverdue} days overdue
                          </Typography>
                        ) : task.daysUntilDeadline !== undefined ? (
                          <Typography variant="body2" color="text.secondary">
                            {task.daysUntilDeadline} days until deadline
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    {/* Action sub-rows */}
                    {actions.map((action: TaskViewActionItem) => {
                      const isMyAction = action.assignedToCurrentUser === true;
                      const canComplete =
                        isMyAction && action.status === 'IN_PROGRESS';
                      const assignedLabel =
                        action.assignedUsers && action.assignedUsers.length > 0
                          ? action.assignedUsers.map((u) => u.displayName || u.email).filter(Boolean).join(', ')
                          : null;
                      return (
                        <TableRow
                          key={`${task.taskId}-${action.actionId}`}
                          sx={{
                            backgroundColor: isMyAction ? 'action.selected' : undefined,
                            borderLeft: isMyAction ? '4px solid' : undefined,
                            borderLeftColor: isMyAction ? 'primary.main' : undefined,
                          }}
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('a')) return;
                            handleRowClick(task);
                          }}
                        >
                          <TableCell sx={{ borderTop: 0 }} />
                          <TableCell sx={{ borderTop: 0 }} />
                          <TableCell sx={{ borderTop: 0 }} />
                          <TableCell sx={{ borderTop: 0, pl: 4 }}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                ↳ {action.name || action.actionType || `Action ${action.actionId}`}
                                {action.actionType ? ` (${action.actionType})` : ''}
                              </Typography>
                              {assignedLabel && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                  Assigned: {assignedLabel}
                                </Typography>
                              )}
                              {isMyAction && (action.documentTitle || action.documentDescription) && (
                                <Box sx={{ mt: 0.5 }}>
                                  {action.documentTitle && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Document: {action.documentTitle}
                                    </Typography>
                                  )}
                                  {action.documentDescription && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Instructions: {action.documentDescription}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderTop: 0 }}>
                            <Chip
                              label={(action.status || 'NOT_STARTED').replace('_', ' ')}
                              size="small"
                              color={getStatusColor(action.status)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ borderTop: 0 }} />
                          <TableCell sx={{ borderTop: 0 }} onClick={(e) => e.stopPropagation()}>
                            {canComplete ? (
                              <Link
                                to={`/create-action?projectId=${task.projectId}&actionId=${action.actionId}`}
                                style={{ textDecoration: 'none' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={<OpenInNew sx={{ fontSize: '0.875rem' }} />}
                                  sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                >
                                  Open Form
                                </Button>
                              </Link>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {expanded && pagination.totalPages > 1 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
      </Collapse>
    </Paper>
  );
}

